/**
 * <mb-ai-summary> — "live generation" presentation for the PDP AI summary.
 * ─────────────────────────────────────────────────────────────────────────────
 * The summary text is NOT generated at runtime — it ships in the page from
 * product.metafields.aigen.desc_summary. What this element adds is the moment of
 * value: the customer presses "Summarize with AI" and watches it get written.
 *
 * Required structure (see snippets/modern-product-accordion.liquid):
 *
 *   <mb-ai-summary data-product-key="123">
 *     <button data-mb-ai-generate>…</button>
 *     <template data-mb-ai-source> rich-text summary markup </template>
 *     <div data-mb-ai-card hidden>
 *       …
 *       <div data-mb-ai-output></div>
 *       <p data-mb-ai-note hidden>…</p>
 *     </div>
 *     <div data-mb-ai-full>                 ← optional
 *       <div> full description </div>
 *       <button data-mb-ai-expand hidden>
 *         <span data-mb-ai-expand-label>…</span>
 *       </button>
 *     </div>
 *   </mb-ai-summary>
 *
 * The summary lives in a <template> so it is inert until generated: not
 * rendered, not read by assistive tech, no images fetched.
 *
 * Once the summary exists, the full description ([data-mb-ai-full]) collapses
 * to a faded preview behind a "Show full description" toggle — summarizing
 * must make the panel shorter, not longer. Before generation (and whenever
 * this script never runs) the description stays fully visible.
 *
 * All layout changes land in ONE moment — when typing starts: the CTA hides,
 * the card appears at final height (pending blocks reserve space via
 * visibility, not display), and the description collapses. Finishing only
 * removes the caret, so nothing jumps when the animation ends.
 *
 * Typing walks TEXT NODES rather than slicing innerHTML — the summary is rich
 * text (<p>, <ul><li>) and slicing markup mid-tag would tear it apart. Each text
 * node is blanked, then refilled character by character in document order, so
 * paragraphs and bullets land in sequence with their markup intact.
 *
 * Replays once per product per session (sessionStorage); afterwards the summary
 * renders instantly. prefers-reduced-motion skips straight to the final state.
 */

const MB_AI_RATE = 45; // characters per second
const MB_AI_THINK_MS = 700; // "Analyzing…" dwell before typing starts
const MB_AI_STORE_PREFIX = 'mb-ai-summary:';

// sessionStorage throws in some privacy modes — a failed read/write must never
// break the interaction, it just means the effect replays.
const mbAiSeen = (key) => {
  try {
    return sessionStorage.getItem(MB_AI_STORE_PREFIX + key) === '1';
  } catch {
    return false;
  }
};

const mbAiRemember = (key) => {
  try {
    sessionStorage.setItem(MB_AI_STORE_PREFIX + key, '1');
  } catch {
    /* ignore */
  }
};

class MBAiSummary extends HTMLElement {
  #cleanups = [];
  #btn = null;
  #template = null;
  #card = null;
  #output = null;
  #note = null;
  #full = null;
  #expandBtn = null;
  #expandLabel = null;
  #running = false;
  #frame = 0;

  connectedCallback() {
    this.#btn = this.querySelector('[data-mb-ai-generate]');
    this.#template = this.querySelector('[data-mb-ai-source]');
    this.#card = this.querySelector('[data-mb-ai-card]');
    this.#output = this.querySelector('[data-mb-ai-output]');
    this.#note = this.querySelector('[data-mb-ai-note]');
    this.#full = this.querySelector('[data-mb-ai-full]');
    this.#expandBtn = this.querySelector('[data-mb-ai-expand]');
    this.#expandLabel = this.querySelector('[data-mb-ai-expand-label]');

    if (!this.#btn || !this.#template || !this.#card || !this.#output) return;

    if (this.#expandBtn) {
      const onExpand = () => this.#toggleFull();
      this.#expandBtn.addEventListener('click', onExpand);
      this.#cleanups.push(() => this.#expandBtn.removeEventListener('click', onExpand));
      this.#bypassEnter(this.#expandBtn, onExpand);
    }

    // Already generated this session → skip the theatre, show the result.
    if (mbAiSeen(this.dataset.productKey || 'default')) {
      this.#revealInstantly();
      return;
    }

    const onClick = () => this.#generate();
    this.#btn.addEventListener('click', onClick);
    this.#cleanups.push(() => this.#btn.removeEventListener('click', onClick));
    this.#bypassEnter(this.#btn, onClick);
  }

  /**
   * Enter bypass — same rogue-script symptom documented in
   * details-disclosure.js: an on-page script re-dispatches Enter as TWO
   * clicks, so a toggle nets to no visible change (measured on the expand
   * button: one Enter press → two click events). Capture-phase keydown runs
   * the action once ourselves and stops the event before that script sees
   * it. Space is left native — it isn't intercepted. The generate CTA only
   * survives today because #generate is idempotent; it gets the same guard
   * so it stops relying on luck.
   */
  #bypassEnter(btn, action) {
    const onKeydown = (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      action();
    };
    btn.addEventListener('keydown', onKeydown, true);
    this.#cleanups.push(() => btn.removeEventListener('keydown', onKeydown, true));
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.#frame);
    this.#cleanups.forEach((fn) => fn());
    this.#cleanups = [];
  }

  // ── Rendering ──────────────────────────────────────────────────────────────

  /** Drop the finished summary in with no animation. */
  #revealInstantly() {
    this.#output.replaceChildren(this.#template.content.cloneNode(true));
    const hadFocus = document.activeElement === this.#btn;
    this.#card.hidden = false;
    if (this.#note) this.#note.hidden = false;
    this.#btn.hidden = true;
    this.#collapseFull();
    if (hadFocus) this.#focusCard();
  }

  /**
   * Hiding the CTA while it holds focus would drop focus to <body> — a
   * keyboard or SR user would lose their place entirely (WCAG 2.4.3). The
   * card takes it instead: tabindex="-1" makes a non-interactive container
   * programmatically focusable without joining the tab order, so the next
   * Tab lands naturally on the expand toggle below.
   */
  #focusCard() {
    this.#card.setAttribute('tabindex', '-1');
    this.#card.focus({ preventScroll: true });
  }

  /**
   * Fold the full description down to its faded preview and surface the
   * toggle. Only ever called once a summary is on screen — before that the
   * description must stay fully visible (nothing hides behind the AI CTA).
   */
  #collapseFull() {
    if (!this.#full) return;
    this.#full.classList.add('mb-aisum__full--collapsed');
    if (this.#expandBtn) {
      this.#expandBtn.hidden = false;
      this.#expandBtn.setAttribute('aria-expanded', 'false');
    }
    if (this.#expandLabel) this.#expandLabel.textContent = 'Show full description';
    this.#syncClippedFocusables();
  }

  #toggleFull() {
    if (!this.#full) return;
    const collapsed = this.#full.classList.toggle('mb-aisum__full--collapsed');
    this.#expandBtn?.setAttribute('aria-expanded', String(!collapsed));
    if (this.#expandLabel) {
      this.#expandLabel.textContent = collapsed ? 'Show full description' : 'Show less';
    }
    this.#syncClippedFocusables();
  }

  /**
   * Keyboard parity with the visual clip: descriptions can contain links, and
   * ones sitting below the collapsed fold are invisible but would still be
   * tabbable — focus would land on nothing. Take those (and only those) out
   * of the tab order while collapsed; restore them on expand.
   */
  #syncClippedFocusables() {
    const body = this.#full?.querySelector('.mb-aisum__full-body');
    if (!body) return;
    const collapsed = this.#full.classList.contains('mb-aisum__full--collapsed');
    const fold = body.getBoundingClientRect().top + body.clientHeight;
    for (const el of body.querySelectorAll('a[href], button, input, select, textarea, [tabindex]')) {
      if (collapsed && el.getBoundingClientRect().top >= fold) {
        el.setAttribute('tabindex', '-1');
        el.setAttribute('data-mb-ai-clipped', '');
      } else if (el.hasAttribute('data-mb-ai-clipped')) {
        el.removeAttribute('tabindex');
        el.removeAttribute('data-mb-ai-clipped');
      }
    }
  }

  // The CTA and note were already swapped at #type() time — finishing only
  // removes the caret, so the layout doesn't jump when typing ends.
  #finish() {
    this.#running = false;
    this.#card.setAttribute('aria-busy', 'false');
    this.querySelector('.mb-aisum__caret')?.remove();
    mbAiRemember(this.dataset.productKey || 'default');
    this.#announce();
  }

  /** One-shot SR announcement so the result isn't silently swapped in. */
  #announce() {
    const status = document.createElement('p');
    status.className = 'visually-hidden';
    status.setAttribute('role', 'status');
    this.#card.appendChild(status);
    // Text lands a beat AFTER the live region exists — some SR/browser pairs
    // skip regions that are inserted with their text already in place.
    const timer = setTimeout(() => {
      status.textContent = 'AI summary ready';
    }, 80);
    this.#cleanups.push(() => clearTimeout(timer));
  }

  // ── Generation flow ────────────────────────────────────────────────────────

  /**
   * GA4 event, same shape as product_siblings_click in modern-product-carousel.js.
   * Fires only on a real click — not on the instant session-replay path — so the
   * count reflects customers who actually asked for a summary.
   */
  #track() {
    if (typeof window.gtag !== 'function') return;
    window.gtag('event', 'pdp_ai_summary_click', {
      product_id: this.dataset.productKey || '',
      product_handle: this.dataset.productHandle || '',
      product_title: this.dataset.productTitle || '',
      page_path: window.location.pathname,
    });
  }

  #generate() {
    if (this.#running || this.#btn.hidden) return;
    this.#running = true;
    this.#track();

    // aria-disabled, not `disabled` — the button must stay focusable so the
    // "Analyzing…" state is readable (CLAUDE.md §2 item 6).
    this.#btn.setAttribute('aria-disabled', 'true');
    this.#btn.classList.add('mb-aisum__cta--busy');
    const label = this.#btn.querySelector('[data-mb-ai-label]');
    if (label) label.textContent = 'Analyzing product details';

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#revealInstantly();
      this.#running = false;
      mbAiRemember(this.dataset.productKey || 'default');
      this.#announce();
      return;
    }

    const timer = setTimeout(() => this.#type(), MB_AI_THINK_MS);
    this.#cleanups.push(() => clearTimeout(timer));
  }

  #type() {
    this.#output.replaceChildren(this.#template.content.cloneNode(true));

    // One layout moment: CTA out, card in (at final height — pending blocks
    // below keep their space via visibility), note visible, description
    // folded. From here on only text fills in; nothing else moves.
    const hadFocus = document.activeElement === this.#btn;
    this.#btn.hidden = true;
    this.#card.hidden = false;
    this.#card.setAttribute('aria-busy', 'true');
    if (this.#note) this.#note.hidden = false;
    this.#collapseFull();
    if (hadFocus) this.#focusCard();

    // Blank every text node, remembering what belongs there.
    const walker = document.createTreeWalker(this.#output, NodeFilter.SHOW_TEXT);
    const nodes = [];
    let n;
    while ((n = walker.nextNode())) {
      const full = n.nodeValue;
      if (!full.trim()) continue; // whitespace between tags — leave as-is
      nodes.push({ node: n, full, block: n.parentElement?.closest('p, li, h1, h2, h3, h4, div') });
      n.nodeValue = '';
    }

    if (!nodes.length) {
      this.#finish();
      return;
    }

    // Hide the blocks so empty <li> bullets don't sit there before their turn.
    const blocks = new Set(nodes.map((e) => e.block).filter(Boolean));
    blocks.forEach((b) => b.setAttribute('data-mb-ai-pending', ''));

    const caret = document.createElement('span');
    caret.className = 'mb-aisum__caret';
    caret.setAttribute('aria-hidden', 'true');

    let i = 0; // node index
    let c = 0; // char index within the node
    let last = performance.now();
    let credit = 0;

    const step = (now) => {
      // Time-based so the pace holds regardless of frame rate; jitter keeps it
      // from reading as a metronome.
      const dt = (now - last) / 1000;
      last = now;
      credit += dt * MB_AI_RATE * (0.85 + Math.random() * 0.3);

      while (credit >= 1 && i < nodes.length) {
        const entry = nodes[i];
        if (c === 0) {
          entry.block?.removeAttribute('data-mb-ai-pending');
          entry.block?.appendChild(caret);
        }
        c += 1;
        entry.node.nodeValue = entry.full.slice(0, c);
        credit -= 1;
        if (c >= entry.full.length) {
          i += 1;
          c = 0;
        }
      }

      if (i < nodes.length) {
        this.#frame = requestAnimationFrame(step);
      } else {
        this.#finish();
      }
    };

    this.#frame = requestAnimationFrame(step);
  }
}

customElements.define('mb-ai-summary', MBAiSummary);
